import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { lucideArrowLeft, lucideAlertTriangle } from '@ng-icons/lucide';
import { PageHeaderComponent, InlineAlertComponent } from '@shared/ui/components';
import { OrderService } from '../../services/order.service';
import { ParcelDto, RepackUpdateRequestDto, ParcelActionDto, EligibleCartonDto, EligibleCartonsRequest } from '../../../models/order.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { RepackingStateService, PendingRepackState } from '../../services/repacking-state.service'; // Import service and state
import { FindPipe } from '../../pipes/find.pipes';

interface RepackAddToExistingState {
  parcelsToAdd: ParcelDto[];
  originalShopeeOrderNo?: string; // ILH Shopee No of the original order (parent of the cancelled carton)
  originalOrderId?: string;       // Unique ID of the original order
}

@Component({
  selector: 'app-repack-add-to-existing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FindPipe,
    ...HlmCardImports,
    HlmButton,
    NgIcon,
    HlmIcon,
    PageHeaderComponent,
    InlineAlertComponent,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideAlertTriangle })],
  templateUrl: './repack-add-to-existing.component.html',
  styleUrls: ['./repack-add-to-existing.component.css']
})
export class RepackAddToExistingComponent implements OnInit {
  parcelsToAdd: ParcelDto[] = [];
  originalOrderId: string | undefined;
  originalShopeeOrderNo: string | undefined;
  originalServiceCode: string = '';
  originalDestinationRegion: string = '';

  targetCartonDetails: {
    originalRepackedCartonNo: string; // This is the ID of the EXISTING carton to add to
    ilhShopeeNo: string; // ILH Shopee No of the EXISTING carton to add to
    // Optional fields if the user can update them during this operation
    cartonWeight: number;
    cartonLength: number;
    cartonWidth: number;
    cartonHeight: number;
    destinationRegion?: string;
    cartonVolume?: number; // Optional, can be calculated or left to backend
  } = {
    originalRepackedCartonNo: '',
    ilhShopeeNo: '',
    cartonWeight: 0,
    cartonLength: 0,
    cartonWidth: 0,
    cartonHeight: 0,
  };

  isLoading = false;
  isLoadingCartons: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  eligibleCartons: EligibleCartonDto[] = [];
  currentPage: number = 1;
  pageSize: number = 10; 
  totalPages: number = 0;
  totalItems: number = 0;
  selectedCartonId: string = '';
  constructor(
    private router: Router,
    private orderService: OrderService,
    public repackingStateService: RepackingStateService // Make it public for template access
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      const state = navigation.extras.state as RepackAddToExistingState;
      this.parcelsToAdd = state.parcelsToAdd || [];
      this.originalOrderId = state.originalOrderId;
      // Note: originalShopeeOrderNo from state is for the parent order, not the target carton.
      // The ILH Shopee No for the target carton needs to be fetched or entered by the user.
    }
  }

  ngOnInit(): void {
   const pendingState = this.repackingStateService.getPendingRepackState();
    if (pendingState) {
      this.parcelsToAdd = pendingState.parcelsForRepack; // These are the parcels from the cancelled carton
      this.originalOrderId = pendingState.sourceOrderUniqueId; // ID of the parent order
      this.originalShopeeOrderNo = pendingState.cancelledCartonIlhShopeeNo;
      this.originalServiceCode = pendingState.cartonConfigToReuse?.serviceCode || '';
      this.originalDestinationRegion = pendingState.cartonConfigToReuse?.destinationRegion || '';

      console.log("DEBUG: Loaded state:", JSON.stringify(pendingState, null, 2));
      console.log("DEBUG: Original service code:", this.originalServiceCode);
      console.log("DEBUG: Original destination region:", this.originalDestinationRegion);
      // Load eligible cartons
      this.loadEligibleCartons();
    }

    else {
      this.errorMessage = "No pending repack information found. Please start the repack process again.";
    }
  }

  // Placeholder: In a real app, you might fetch target carton details
  // when originalRepackedCartonNo is entered to prefill ilhShopeeNo or other fields.

  loadEligibleCartons(page: number = 1): void {
  if (!this.originalServiceCode) {
    this.errorMessage = "Missing service code from original carton. Cannot find eligible cartons.";
    return;
  }
    
    this.isLoadingCartons = true;
    // Create the request object
    const request: EligibleCartonsRequest = {
      serviceCode: this.originalServiceCode,
      // Add destination region if available
      destinationRegion: this.originalDestinationRegion,
      // Add original carton ID to exclude it from results
      originalCartonId: this.originalOrderId,
      page: page,
      pageSize: this.pageSize
    };

    this.orderService.getEligibleCartonsForRepack(request).subscribe({
        next: (response) => {
          console.log("DEBUG: Eligible cartons response:", response);
          
          if (response && response.items && response.items.length > 0) {
            this.eligibleCartons = response.items;
            this.totalItems = response.totalCount;
            this.totalPages = response.totalPages;
            this.currentPage = response.pageNumber;
            
            // Add helpful summary text to each option
            this.eligibleCartons.forEach(carton => {
              carton.summary = `${carton.cartonNo} (${carton.shopeeOrderNo || 'No ILH'}) - ${carton.parcelCount} parcels - ${carton.status}`;
            });
            
            console.log("DEBUG: Processed eligible cartons:", this.eligibleCartons);
          } else {
            this.eligibleCartons = [];
            this.errorMessage = "No eligible cartons found with matching service code.";
          }
          
          this.isLoadingCartons = false;
        },
        error: (err) => {
          console.error("Error loading eligible cartons:", err);
          this.errorMessage = `Failed to load eligible cartons: ${err.message}`;
          this.isLoadingCartons = false;
        }
      });
    }

    // Add method to handle page changes
    changePage(page: number): void {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.loadEligibleCartons(page);
    }


  onSubmit(): void {
      if (!this.selectedCartonId) {
        this.errorMessage = "Please select a target carton.";
        return;
      }
      
      if (this.parcelsToAdd.length === 0) {
        this.errorMessage = "No parcels to add to the target carton.";
        return;
      }
      
      this.isLoading = true;
      this.errorMessage = null;
      this.successMessage = null;
      
      const selectedCarton = this.eligibleCartons.find(c => c.cartonNo === this.selectedCartonId);
      if (!selectedCarton) {
        this.errorMessage = "Selected carton not found in eligible cartons list.";
        this.isLoading = false;
        return;
      }
      
      // Create request to add parcels to existing carton
       const request: RepackUpdateRequestDto = {
        originalRepackedCartonNo: selectedCarton.cartonNo,  // camelCase
        ilhShopeeNo: selectedCarton.shopeeOrderNo || '',    // camelCase
        destinationRegion: selectedCarton.destinationRegion, // camelCase
        cartonWeight: selectedCarton.physicalDetails?.weight || 0,
        cartonLength: selectedCarton.physicalDetails?.length || 0,
        cartonWidth: selectedCarton.physicalDetails?.width || 0,
        cartonHeight: selectedCarton.physicalDetails?.height || 0,
        cartonVolume: selectedCarton.physicalDetails?.volume || 0,
        parcelActions: this.parcelsToAdd.map(parcel => ({   // camelCase
          parcelTrackingNumber: parcel.trackingNumber || '', // camelCase
          actionType: 1 // 1 = Add
        }))
      };

      console.log("DEBUG: Submitting request:", JSON.stringify(request, null, 2));
      
      this.orderService.repackUpdate(request).subscribe({
        next: (response) => {
        console.log("DEBUG: Update response:", response);
        this.isLoading = false;
        this.successMessage = `Successfully added ${this.parcelsToAdd.length} parcel(s) to carton ${selectedCarton.cartonNo}.`;
        
        // Clear the pending states
        this.repackingStateService.clearPendingRepackState();
        this.repackingStateService.clearPendingRebindState();
        
        // Add a delay before navigating
        setTimeout(() => {
          this.router.navigate(['/dashboard/orders']);
        }, 2000);
      },
      error: (err) => {
        console.error("Error adding parcels:", err);
        this.isLoading = false;
        this.errorMessage = `Failed to add parcels to carton: ${err.message}`;
      }
      });
    }

  // Add a goBack method with warning for pending rebind
  goBack(): void {
    // Check if there are pending rebinds
    if (this.repackingStateService.hasPendingRebind()) {
      const confirmLeave = window.confirm(
        'You have unbound parcels that need to be added to a carton. ' +
        'Going back now will leave these parcels unassigned. ' +
        'Are you sure you want to continue?'
      );
      
      if (!confirmLeave) {
        return;
      }
      
      // User confirmed to leave, clear the pending states
      this.repackingStateService.clearPendingRepackState();
      this.repackingStateService.clearPendingRebindState();
    }
    
    // Navigate back to orders list
    this.router.navigate(['/dashboard/orders']);
  }

  // Helper method to check for pending rebinds - can be used in template
  hasPendingRebind(): boolean {
    return this.repackingStateService.hasPendingRebind();
  }
}



